import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import {
	CrmLoadError,
	CrmPageHeader,
	CrmRoleNotice,
} from "@/components/crm/crm-page";
import { ToneBadge } from "@/components/crm/status-badges";
import { TaskForm } from "@/components/crm/task-form";
import { TaskItem, useToggleTask } from "@/components/crm/task-list";
import { UnlinkedEmails } from "@/components/crm/unlinked-emails";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useCrmPermissions } from "@/hooks/use-crm-access";
import { CRM_STALE_TIME } from "@/lib/crm/queries";
import { groupTasks, TASK_GROUPS } from "@/lib/crm/tasks";
import { orpc } from "@/utils/orpc";

export const Route = createFileRoute("/crm/tasks")({
	component: MyTasksPage,
});

function MyTasksPage(): ReactElement {
	const { t } = useTranslation();
	const { canManage } = useCrmPermissions();
	const toggle = useToggleTask();
	const { data, isLoading, isError } = useQuery({
		...orpc.admin.crm.tasks.mine.queryOptions(),
		enabled: canManage,
		staleTime: CRM_STALE_TIME,
	});

	if (!canManage) return <CrmRoleNotice />;

	const groups = groupTasks(data?.tasks ?? []);

	// A task list reads best narrow: the form and the lists share one width.
	return (
		<div className="max-w-3xl space-y-4">
			<CrmPageHeader
				title={t("admin:crm.tasks.my_title")}
				subtitle={t("admin:crm.tasks.my_subtitle")}
			/>
			<UnlinkedEmails />
			<Card>
				<CardContent>
					<TaskForm />
				</CardContent>
			</Card>
			{isLoading ? (
				<Skeleton className="h-40" />
			) : isError || !data ? (
				<CrmLoadError />
			) : data.tasks.length === 0 ? (
				<p className="py-8 text-center text-muted-foreground text-sm">
					{t("admin:crm.tasks.all_done")}
				</p>
			) : (
				TASK_GROUPS.filter((group) => groups[group].length > 0).map((group) => (
					<Card key={group} data-testid={`task-group-${group}`}>
						<CardHeader className="pb-2">
							<CardTitle className="flex items-center gap-2 text-base">
								{t(`admin:crm.tasks.group.${group}`)}
								<ToneBadge
									tone={
										group === "overdue"
											? "rose"
											: group === "today"
												? "amber"
												: "slate"
									}
								>
									{groups[group].length}
								</ToneBadge>
							</CardTitle>
						</CardHeader>
						<CardContent>
							<ul className="divide-y">
								{groups[group].map((task) => (
									<TaskItem
										key={task.id}
										task={task}
										showParent
										onToggle={(row) => void toggle(row)}
									/>
								))}
							</ul>
						</CardContent>
					</Card>
				))
			)}
		</div>
	);
}
